import React, { useEffect, useState } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import { HiOutlineChevronDown } from "react-icons/hi2";
import DashboardCard from "./DashboardCard";
import { useHolidayActivities } from "../../home/people/hooks";
import moment from "moment";
import Loader from "../../../components/loader";
import api from "../../../services/api";

import { differenceDays } from "../../home/people/hooks";

const TimeOffCard = ({ user, filters, businessUnit }) => {
  const { hollidayActivities, peopleCurrentlyOff } = useHolidayActivities();
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const userQuery = {
          role: ["admin", "user", "super_admin"],
          contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
          status: "active",
        };

        // Add manager filter only if a user is selected
        if (user) {
          userQuery.manager_id = user._id;
          userQuery.managees_of_managees = filters.manageesOfManagees;
        }

        // Filter by business unit
        if (businessUnit._id !== "ALL") {
          userQuery.business_unit_id = businessUnit._id;
        }

        const { data: usersData, ok } = await api.post(`/user/search`, userQuery);
        
        if (!ok || !usersData?.users) throw new Error("Error fetching users for time off"); 

        setTeamMembers(usersData.users);
      } catch (error) {
        toast.error(error.message);
      }
    };

    fetchTeamMembers();
  }, [user?._id, filters, businessUnit]);

  if (!hollidayActivities) return <Loader />;

  const filteredHolidayActivities = hollidayActivities.filter((activity) => teamMembers.some((member) => member._id === activity.userId));

  return (
    <DashboardCard title="Time Off" icon={<FaCalendarAlt className="text-green-500" />}>
      <div className="space-y-2">
        {filteredHolidayActivities
          .sort((a, b) => {
            const nextHollidayA = differenceDays(new Date(), a.ranges[0].start);
            const nextHollidayB = differenceDays(new Date(), b.ranges[0].start);
            return nextHollidayA - nextHollidayB;
          })
          .map((timeOff, index) => (
            <TimeOffItem key={index} timeOff={timeOff} teamMember={teamMembers.find((member) => member._id === timeOff.userId)} />
          ))}
      </div>
    </DashboardCard>
  );
};

const TimeOffItem = ({ timeOff, teamMember }) => {
  const [open, setOpen] = useState(false);
  const daysCount = timeOff.ranges.reduce((acc, range) => acc + differenceDays(range.start, range.end) + 1, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          <img src={teamMember?.avatar} alt={timeOff.userName} className="w-8 h-8 rounded-full object-cover border border-gray-100" />
          <div>
            <h3 className="text-sm font-medium text-gray-800">{timeOff.userName}</h3>
            <p className="text-xs text-gray-500">{teamMember?.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-end gap-1">
            <div className="text-xs text-gray-600">{daysCount} days</div>
            <div className="text-xs text-gray-600">next: {moment(timeOff.slots[0]).format("DD MMM")}</div>
          </div>
          <HiOutlineChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 p-2">
          <div className="space-y-1">
            {timeOff.ranges.map((range, index) => (
              <div key={index} className="flex justify-between items-center p-1.5 bg-gray-50 rounded text-xs">
                <div>
                  <p className="font-medium text-gray-800">
                    {moment(range.start).format("DD MMM YYYY")}
                    {range.start !== range.end && ` - ${moment(range.end).format("DD MMM YYYY")}`}
                  </p>
                  <p className="text-gray-600">{differenceDays(range.start, range.end) + 1} days</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-800">{differenceDays(range.start, range.end) + 1}d</p>
                  <p className="text-gray-500">{range.start === range.end ? "Single Day" : "Multiple Days"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeOffCard;
